import { useMutation } from '@apollo/client'

import { gql } from '@web/gql'
import { Variant } from '@web/gql/graphql'
import { IS_BETA, IS_DEV } from '@web/libs/constants'
import { neloLogger } from '@web/libs/utils'

const trackEventMutation = gql(`
  mutation TrackEvent($TrackEventInput: TrackEventInput!) {
        trackEvent(input: $TrackEventInput) {
          __typename
        }
      }
`)

const trackCustomEventMutation = gql(`
  mutation TrackCustomEvent($TrackCustomEventInput: TrackCustomEventInput!) {
        trackCustomEvent(input: $TrackCustomEventInput) {
          __typename
        }
      }
`)

const ABT_REVIEW_WRITE_EVENT_TYPE = 'ud_review_write'

const useAbTestTrack = () => {
  const [trackEvt] = useMutation(trackEventMutation)
  const [trackCustomEvt] = useMutation(trackCustomEventMutation)

  const trackNClickEvent = (variant: Variant, clickCode: string): void => {
    const { expId, name: variantName, sessionId } = variant

    if (!variantName || !sessionId) return

    trackEvt({
      variables: { TrackEventInput: { expId, clickCode, variantName, sessionId } },
    })
  }

  const trackCustomEvent = (
    variant: Variant,
    { eventKey, eventType, onceKey }: { eventKey: string; eventType: string; onceKey?: string },
  ): void => {
    const { expId, name: variantName, sessionId } = variant

    if (!variantName || !sessionId) return

    // 특정 이벤트를 해당 세션에 한번만 전송하는걸 보장하고 싶은경우
    if (onceKey) {
      const key = `trackCustomEvent:${eventType}_${eventKey}_${sessionId}_${onceKey}`
      const alreadyEventTracked = window?.sessionStorage.getItem(key)

      if (alreadyEventTracked) return
      window?.sessionStorage.setItem(key, 'tracked')
    }

    if (IS_DEV || IS_BETA)
      neloLogger('info', `[trackCustomEvent] ${eventType} ${eventKey} `, {
        expId,
        variantName,
        sessionId,
        eventKey,
        eventType,
        onceKey,
      })

    trackCustomEvt({
      variables: { TrackCustomEventInput: { expId, variantName, sessionId, eventKey, eventType } },
    })
  }

  const trackReviewWrite = async (
    variant: Variant,
    {
      isHighQuality,
      isNatureReview,
      reviewGroupId,
    }: { isHighQuality?: boolean; isNatureReview?: boolean; reviewGroupId: string },
  ) => {
    if (!variant) return

    await trackCustomEvent(variant, {
      eventType: ABT_REVIEW_WRITE_EVENT_TYPE,
      eventKey: 'review_write',
      onceKey: reviewGroupId,
    })

    if (isHighQuality) {
      await trackCustomEvent(variant, {
        eventType: ABT_REVIEW_WRITE_EVENT_TYPE,
        eventKey: 'review_write_high_quality',
        onceKey: reviewGroupId,
      })
    }

    if (isNatureReview) {
      await trackCustomEvent(variant, {
        eventType: ABT_REVIEW_WRITE_EVENT_TYPE,
        eventKey: 'review_write_nature',
        onceKey: reviewGroupId,
      })
    }
  }

  return {
    trackNClickEvent,
    trackCustomEvent,
    trackReviewWrite,
  }
}

export default useAbTestTrack
